import { Button } from "@/components/ui/button"
import { Typography } from "@/components/ui/typography"
import { SETTINGS_PATHS } from "@/features/settings/paths"
import { useObjectUrl } from "@/hooks/useObjectUrl"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import {
  useOrgSignatureImage,
  useOrgSignatures,
  type OrganizationSignature,
} from "@/services/api/organizationSignaturesApi"
import { ArrowUpRight, Check, Loader2, Plus } from "lucide-react"
import { lazy, Suspense, useState } from "react"
import { Link } from "react-router"

// Pulls react-signature-canvas, react-image-crop and the bundled Caveat
// webfont — none of which any other settings page needs — so it only loads
// once the user actually opens it. Same lazy import as the My signatures
// settings page.
const AddSignatureDialog = lazy(() => import("./AddSignatureDialog"))

const ACTION_CLASS = "h-7 px-2 text-[11px] text-grey5 hover:text-white"

/**
 * The caller's OWN saved signatures as a grid of selectable cards, shared by
 * the conformity editor's per-declaration signature field and the template
 * editor's "Default signature" (design §5). A signature image is personal,
 * not shared org branding — `useOrgSignatures` never surfaces a colleague's
 * rows even though the endpoint is `/organization/signatures`.
 *
 * `value`/`onChange` are id-based, not index-based — `onChange(null)` is the
 * explicit "None" affordance, distinct from a click on an already-selected
 * card (which the caller's own guard should no-op, same as before this was
 * extracted: this component always calls `onChange` on a card click, even
 * the selected one, and leaves de-duplication to the caller).
 *
 * A change in flight is two props, because disabling and the spinner have
 * different scopes: `pending` disables EVERY card and the Remove action (one
 * change at a time, no double submit), while `pendingId` names the one
 * target that shows the spinner. `pendingId` is the argument of the
 * in-flight `onChange` call: a signature id spins that card, `null` spins
 * the Remove action (which keeps its label). It is only read while
 * `pending` is set; left out, the picker is disabled with no spinner at all.
 * Callers own the in-flight bookkeeping (which target, and for how long,
 * e.g. through the refetch that moves the checkmark).
 */
export function SignaturePicker({
  value,
  onChange,
  pending = false,
  pendingId,
  allowAdd = false,
  disabled = false,
}: {
  value: string | null
  onChange: (id: string | null) => void
  pending?: boolean
  pendingId?: string | null
  allowAdd?: boolean
  disabled?: boolean
}) {
  const { translate } = useTranslation()
  const { data: signatures, isLoading } = useOrgSignatures()
  const [addOpen, setAddOpen] = useState(false)
  const removing = pending && pendingId === null

  const addButton = allowAdd && (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={() => setAddOpen(true)}
      disabled={disabled}
      className={ACTION_CLASS}
    >
      <Plus size={12} />
      {translate("MC_ORG_SIG_ADD")}
    </Button>
  )

  const addDialog = addOpen && (
    <Suspense fallback={null}>
      <AddSignatureDialog open={addOpen} onOpenChange={setAddOpen} />
    </Suspense>
  )

  if (isLoading) {
    return (
      <Typography
        as="muted"
        className="flex items-center gap-1.5 text-[11px] text-grey5"
      >
        <Loader2 size={11} className="animate-spin" />
        {translate("MC_CS_CONF_EDIT_SIGN_LOADING")}
      </Typography>
    )
  }

  if (!signatures || signatures.length === 0) {
    return (
      <div className="space-y-1.5">
        <Typography as="muted" className="text-[11px] text-grey5">
          {translate("MC_CS_CONF_EDIT_SIGN_EMPTY")}
        </Typography>
        {allowAdd ? (
          addButton
        ) : (
          <Link
            to={SETTINGS_PATHS.signatures}
            className="inline-flex items-center gap-1 text-[11px] text-grey5 hover:text-white"
          >
            {translate("MC_CS_CONF_EDIT_SIGN_EMPTY_LINK")}
            <ArrowUpRight size={11} className="shrink-0" />
          </Link>
        )}
        {addDialog}
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        {signatures.map((signature) => (
          <SignatureOption
            key={signature.id}
            signature={signature}
            selected={value === signature.id}
            spinning={pending && pendingId === signature.id}
            disabled={disabled || pending}
            onSelect={() => onChange(signature.id)}
          />
        ))}
      </div>
      {/* One row, same button chrome: Remove (or "Not signed yet") and Add. */}
      <div className="flex items-center gap-2">
        {value ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(null)}
            disabled={disabled || pending}
            aria-busy={removing}
            className={ACTION_CLASS}
          >
            {removing && <Loader2 size={11} className="animate-spin" />}
            {translate("MC_CS_CONF_EDIT_SIGN_REMOVE")}
          </Button>
        ) : (
          <Typography as="muted" className="px-2 text-[11px] text-grey5">
            {translate("MC_CS_CONF_EDIT_SIGN_UNSIGNED")}
          </Typography>
        )}
        {addButton}
      </div>
      {addDialog}
    </div>
  )
}

function SignatureOption({
  signature,
  selected,
  spinning,
  disabled,
  onSelect,
}: {
  signature: OrganizationSignature
  selected: boolean
  /** This card's own pick is the change in flight. */
  spinning: boolean
  disabled: boolean
  onSelect: () => void
}) {
  const { translate } = useTranslation()
  const { data: blob } = useOrgSignatureImage(signature.id)
  const imageUrl = useObjectUrl(blob)

  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={disabled}
      aria-pressed={selected}
      aria-busy={spinning}
      aria-label={translate("MC_CS_CONF_EDIT_SIGN_ARIA_SELECT", {
        name: signature.signatoryName,
      })}
      className={cn(
        "flex items-center gap-2 rounded-md border p-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60",
        selected
          ? "border-yellow1 bg-yellow1/10"
          : "border-grey4 bg-grey3 hover:border-grey5",
      )}
    >
      <div className="flex h-8 w-14 shrink-0 items-center justify-center rounded bg-white">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            className="max-h-full max-w-full object-contain"
          />
        ) : (
          <Loader2 size={11} className="animate-spin text-grey5" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <Typography
          as="small"
          className="block truncate leading-tight text-white"
        >
          {signature.signatoryName}
        </Typography>
        {signature.signatoryFunction && (
          <Typography
            as="muted"
            className="block truncate text-[10px] leading-tight"
          >
            {signature.signatoryFunction}
          </Typography>
        )}
      </div>
      {spinning ? (
        <Loader2 size={12} className="shrink-0 animate-spin text-grey5" />
      ) : selected ? (
        <Check size={12} className="shrink-0 text-yellow1" />
      ) : null}
    </button>
  )
}
